import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { Sparkles, Database, Building2, Mail, Target, CheckCircle2 } from "lucide-react";
import { useLang } from "@/i18n/LangContext";

export default function AiChatMockup() {
  const { t } = useLang();
  const c = t("ai.chat");
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const reduce = useReducedMotion();
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (reduce) {
      setStep(6);
      return;
    }
    const timers = [900, 2100, 3400, 4600, 5900, 7100].map((ms, i) =>
      setTimeout(() => setStep(i + 1), ms)
    );
    return () => timers.forEach(clearTimeout);
  }, [inView, reduce]);

  return (
    <div ref={ref} data-testid="ai-chat-mockup" className="relative">
      <div className="rounded-2xl border border-white/10 bg-ink2 shadow-2xl overflow-hidden">
        <div className="flex items-center gap-3 border-b border-white/10 px-5 py-3.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-dblue to-electric">
            <Sparkles className="h-5 w-5 text-white" />
          </span>
          <div>
            <p className="font-display text-sm font-bold">AI SECRETARY</p>
            <p className="text-[10px] tracking-[0.2em] text-electric">24/7 • ONLINE</p>
          </div>
          <span className="ml-auto rounded-full border border-white/10 px-2.5 py-1 text-[10px] text-steel">{c.demo}</span>
        </div>

        <div className="space-y-4 p-5 min-h-[300px]">
          {step >= 1 && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex justify-end">
              <div className="max-w-[80%] rounded-2xl rounded-br-md bg-gradient-to-r from-dblue to-electric px-4 py-3 text-sm text-white">
                {c.customer}
              </div>
            </motion.div>
          )}
          {step === 2 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-2 text-xs text-steel">
              <span className="flex gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-electric animate-bounce" />
                <span className="h-1.5 w-1.5 rounded-full bg-electric animate-bounce [animation-delay:0.15s]" />
                <span className="h-1.5 w-1.5 rounded-full bg-electric animate-bounce [animation-delay:0.3s]" />
              </span>
              {c.typing}
            </motion.div>
          )}
          {step >= 3 && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex">
              <div className="max-w-[80%] rounded-2xl rounded-bl-md border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/90">
                {c.ai}
              </div>
            </motion.div>
          )}

          {step >= 4 && (
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              className="rounded-xl border border-electric/30 bg-ink p-4 space-y-2.5"
            >
              <div className="flex items-center gap-2 text-sm text-white/90"><Building2 className="h-4 w-4 text-electric" /><span className="text-steel text-xs w-20">COMPANY</span>{c.company}</div>
              <div className="flex items-center gap-2 text-sm text-white/90"><Mail className="h-4 w-4 text-electric" /><span className="text-steel text-xs w-20">EMAIL</span>{c.email}</div>
              <div className="flex items-center gap-2 text-sm text-white/90"><Target className="h-4 w-4 text-electric" /><span className="text-steel text-xs w-20">INTEREST</span>{c.interest}</div>
              {step >= 5 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2 border-t border-white/10 pt-2.5">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  <span className="text-xs font-bold tracking-widest text-emerald-400">{c.status}</span>
                </motion.div>
              )}
            </motion.div>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-4">
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-electric/50 to-transparent" />
        <motion.div
          initial={false}
          animate={step >= 6 ? { opacity: 1, scale: 1 } : { opacity: 0.35, scale: 0.97 }}
          className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors duration-500 ${step >= 6 ? "border-electric/50 bg-ink2" : "border-white/10 bg-ink2/50"}`}
        >
          <Database className={`h-5 w-5 ${step >= 6 ? "text-electric" : "text-steel"}`} />
          <div>
            <p className="font-display text-xs font-bold tracking-widest">CRM</p>
            {step >= 6 && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[10px] font-bold tracking-[0.2em] text-emerald-400">
                {c.action}
              </motion.p>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
